/**
 * Blatt von unten (Baukasten) — das gemeinsame Gegenstück zu den früheren Eigenbauten aus
 * `DialogContent … bottom-0`. Am Handy von unten, ab Tablet optional als Panel rechts im Rahmen
 * (`desktop="panel"`). Kopf und Fuß stehen fest, nur der Inhalt scrollt; die Höhe hängt an `dvh`.
 * Fokusfalle und Escape liefert Radix; den Rücksprung zum Auslöser merkt sich das Blatt selbst,
 * weil es oft ohne Radix-Trigger geöffnet wird (FRONTEND_GUIDELINES §8).
 */
import * as Dialog from '@radix-ui/react-dialog';
import { useRef, type ReactNode, type RefObject } from 'react';
import { cn } from '@/shared/lib/cn';

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Die Hauptaktion steht hier und bleibt ohne Scrollen erreichbar. */
  footer?: ReactNode;
  /** Ab 768 px: `sheet` bleibt ein Blatt von unten, `panel` wird zum 440-px-Panel rechts. */
  desktop?: 'sheet' | 'panel';
  className?: string;
  /** Rücksprung, wenn der Auslöser beim Öffnen nicht mehr den Fokus hat (etwa nach einer Prüfung). */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Die Pane bringt Kopf und Schließen selbst mit (Detailhüllen): Titel und Beschreibung bleiben
   * für Vorleser, ein zweiter sichtbarer Kopf entfällt, der Inhalt läuft ohne Rand.
   */
  bare?: boolean;
  /** Nach dem Schließen, wenn der Fokus zurück ist – etwa um danach einen Dialog zu öffnen. */
  onClosed?: () => void;
}

export function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  desktop = 'sheet',
  className,
  returnFocusRef,
  bare = false,
  onClosed,
}: BottomSheetProps) {
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current) {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasOpen.current = open;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-ink/35 fixed inset-0 z-50" />
        <Dialog.Content
          {...(description ? {} : { 'aria-describedby': undefined })}
          onEscapeKeyDown={(event) => {
            // Eine offene Liste im Blatt (Apartmentwahl) schließt mit Escape nur sich selbst.
            const target = event.target;
            if (target instanceof Element && target.closest('[data-own-escape]')) {
              event.preventDefault();
            }
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const target = returnFocusRef?.current;
            (target?.isConnected ? target : opener.current)?.focus();
            onClosed?.();
          }}
          className={cn(
            'text-ink shadow-panel rounded-t-sheet fixed inset-x-0 bottom-0 z-50 flex max-h-[88dvh] flex-col bg-white pb-[env(safe-area-inset-bottom)] font-sans',
            desktop === 'panel' &&
              'md:rounded-sheet md:inset-y-4 md:left-auto md:right-4 md:max-h-none md:w-[440px] md:pb-0',
            className,
          )}
        >
          {bare ? (
            <>
              <Dialog.Title className="sr-only">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="sr-only">{description}</Dialog.Description>
              ) : null}
            </>
          ) : (
            <div className="shrink-0 px-4 pt-2.5 md:px-5 md:pt-4">
              <span
                aria-hidden
                className={cn(
                  'bg-line mx-auto mb-2.5 block h-1 w-10 rounded-full',
                  desktop === 'panel' && 'md:hidden',
                )}
              />
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <Dialog.Title className="font-serif text-xl font-bold">{title}</Dialog.Title>
                  {description ? (
                    <Dialog.Description className="text-ink-soft text-sm">
                      {description}
                    </Dialog.Description>
                  ) : null}
                </div>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="border-hairline rounded-pill flex h-11 flex-none items-center border bg-white px-4 text-[13px] font-bold"
                  >
                    Schließen
                  </button>
                </Dialog.Close>
              </div>
            </div>
          )}
          <div className={cn('min-h-0 flex-1 overflow-y-auto', !bare && 'px-4 py-3 md:px-5')}>
            {children}
          </div>
          {footer ? (
            <div className="border-hairline shrink-0 border-t px-4 py-3 md:px-5">{footer}</div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
